import {
  Group,
  ActionIcon,
  Avatar,
  Menu,
  Box,
  Switch,
  useMantineColorScheme,
  Indicator,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import {
  IconBell,
  IconSettings,
  IconLogout,
  IconUser,
  IconMoon,
} from "@tabler/icons-react";
import { useAuthStore } from "@/stores/auth";
import { CompanySwitcher } from "@/components/layout/CompanySwitcher";
import { PropertySwitcher } from "@/components/layout/PropertySwitcher";
import { ConnectionStatus } from "@/components/common/ConnectionStatus";

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
    : "";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <Box
      px="md"
      py="xs"
      style={{
        borderBottom: "1px solid var(--mantine-color-default-border)",
        height: 56,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Group justify="space-between" w="100%">
        {/* Left: Company + Property switcher */}
        <Group gap="xs">
          <CompanySwitcher />
          <PropertySwitcher />
        </Group>

        {/* Right: Actions */}
        <Group gap="sm">
          {/* Notifications */}
          <Indicator size={8} color="red" offset={4} disabled>
            <ActionIcon variant="subtle" color="gray" aria-label="Notifications">
              <IconBell size={20} stroke={1.5} />
            </ActionIcon>
          </Indicator>

          {/* Connection status */}
          <ConnectionStatus />

          {/* User menu */}
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" radius="xl" size="lg" color="gray">
                <Avatar
                  src={user?.avatarUrl}
                  alt={user ? `${user.firstName} ${user.lastName}` : "User"}
                  radius="xl"
                  size="sm"
                  color="indigo"
                >
                  {initials}
                </Avatar>
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>
                {user ? `${user.firstName} ${user.lastName}` : "Account"}
              </Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={16} stroke={1.5} />}
                onClick={() => navigate("/settings")}
              >
                Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={16} stroke={1.5} />}
                onClick={() => navigate("/settings")}
              >
                Settings
              </Menu.Item>
              <Menu.Item
                leftSection={<IconMoon size={16} stroke={1.5} />}
                rightSection={
                  <Switch
                    size="xs"
                    checked={colorScheme === "dark"}
                    readOnly
                    tabIndex={-1}
                    aria-label="Toggle dark mode"
                    styles={{ track: { cursor: "pointer" } }}
                  />
                }
                onClick={toggleColorScheme}
                closeMenuOnClick={false}
              >
                Dark mode
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={16} stroke={1.5} />}
                color="red"
                onClick={handleLogout}
              >
                Log out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Box>
  );
}
